<template>
  <section id="certifications" class="section">
    <div class="certs__inner">
      <div class="section-header">
        <p class="section-label reveal" ref="el" :class="{ 'is-visible': isVisible }">Certifications</p>
        <h2 class="section-title reveal reveal--d1" :class="{ 'is-visible': isVisible }">
          Verified Credentials
        </h2>
        <p class="section-desc reveal reveal--d2" :class="{ 'is-visible': isVisible }">
          Continuous learning through industry-recognized certifications across
          data engineering, cloud platforms, and programming.
        </p>
      </div>

      <div class="certs__grid">
        <div
          v-for="(cert, idx) in certs"
          :key="cert.title"
          :class="['cert-card', 'card', 'reveal', `reveal--d${(idx % 4) + 1}`, { 'is-visible': isVisible }]"
          @click="openLightbox(idx)"
          role="button"
          :aria-label="`View ${cert.title}`"
        >
          <div class="cert-card__image-wrap">
            <img :src="cert.img" :alt="cert.title" class="cert-card__image" loading="lazy" />
            <div class="cert-card__overlay">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              <span>View Certificate</span>
            </div>
          </div>
          <div class="cert-card__info">
            <span class="tag tag--purple cert-card__issuer">{{ cert.issuer }}</span>
            <h4 class="cert-card__title">{{ cert.title }}</h4>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox -->
    <Teleport to="body">
      <Transition name="lightbox">
        <div v-if="lightboxIdx !== null" class="lightbox" @click.self="closeLightbox">
          <button class="lightbox__close" @click="closeLightbox" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <button class="lightbox__nav lightbox__nav--prev" @click="prevCert" aria-label="Previous">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div class="lightbox__content" v-if="lightboxIdx !== null">
            <img :src="certs[lightboxIdx].img" :alt="certs[lightboxIdx].title" class="lightbox__image" />
            <div class="lightbox__meta">
              <span class="tag tag--purple">{{ certs[lightboxIdx].issuer }}</span>
              <h3 class="lightbox__title">{{ certs[lightboxIdx].title }}</h3>
              <p class="lightbox__counter">{{ lightboxIdx + 1 }} / {{ certs.length }}</p>
            </div>
          </div>
          <button class="lightbox__nav lightbox__nav--next" @click="nextCert" aria-label="Next">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useReveal } from '../composables/useReveal'

const { el, isVisible } = useReveal()

watch(isVisible, (val) => {
  if (val) {
    document.querySelectorAll('#certifications .reveal').forEach((el, i) => {
      setTimeout(() => el.classList.add('is-visible'), i * 60)
    })
  }
})

const lightboxIdx = ref(null)

function openLightbox(idx) { lightboxIdx.value = idx }
function closeLightbox()   { lightboxIdx.value = null }
function nextCert()  { lightboxIdx.value = (lightboxIdx.value + 1) % certs.length }
function prevCert()  { lightboxIdx.value = (lightboxIdx.value - 1 + certs.length) % certs.length }

function onKeyDown(e) {
  if (lightboxIdx.value === null) return
  if (e.key === 'Escape') closeLightbox()
  if (e.key === 'ArrowRight') nextCert()
  if (e.key === 'ArrowLeft')  prevCert()
}

onMounted(() => window.addEventListener('keydown', onKeyDown))
onUnmounted(() => window.removeEventListener('keydown', onKeyDown))

const certs = [
  {
    title: 'Python & Pandas for Data Engineering',
    issuer: 'Coursera',
    img: '/assets/img/certification/Python_and_Panda_for_Data_Engineering.jpg',
  },
  {
    title: 'Data Warehouse: The Ultimate Guide',
    issuer: 'Udemy',
    img: '/assets/img/certification/Data_Warehouse_The_ultimate_guide.jpg',
  },
  {
    title: 'Python Basic Certificate',
    issuer: 'HackerRank',
    img: '/assets/img/certification/python_basic certificate.png',
  },
  {
    title: 'Introduction to Relational Databases',
    issuer: 'IBM / Coursera',
    img: '/assets/img/certification/IntroductiontoRelationalDatabases.jpg',
  },
  {
    title: 'Data Analysis with R Programming',
    issuer: 'Google / Coursera',
    img: '/assets/img/certification/DataAnalysiswithRProgramming.jpg',
  },
  {
    title: 'Python for Data Science, AI & Development',
    issuer: 'IBM / Coursera',
    img: '/assets/img/certification/PythonforData ScienceAIDevelopment.jpg',
  },
  {
    title: 'SQL Intermediate Certificate',
    issuer: 'HackerRank',
    img: '/assets/img/certification/sql_intermediate_hcrank.jpg',
  },
  {
    title: 'Advanced SQL for Data Engineering',
    issuer: 'Coursera',
    img: '/assets/img/certification/Advanced_SQL_for_Data_Engineering.jpg',
  },
  {
    title: 'Databases & SQL for Data Science with Python',
    issuer: 'IBM / Coursera',
    img: '/assets/img/certification/DatabasesandSQ fordataSciencewithPython.jpg',
  },
  {
    title: 'Introduction to Networking',
    issuer: 'Cisco',
    img: '/assets/img/certification/Introduction to Networking.jpg',
  },
  {
    title: 'Linux Crash Course for Beginners',
    issuer: 'Udemy',
    img: '/assets/img/certification/linux crash course for beginners.jpg',
  },
  {
    title: 'Relational Database Administration (DBA)',
    issuer: 'IBM / Coursera',
    img: '/assets/img/certification/RelationalDatabaseAdministration(DBA).jpg',
  },
]
</script>

<style scoped>
.certs__inner { max-width: 1200px; margin: 0 auto; }

.certs__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  padding-right: 4px;
}

.cert-card {
  cursor: pointer;
  padding: 0;
  overflow: hidden;
}
.cert-card:hover { transform: translateY(-4px); }

.cert-card__image-wrap { position: relative; aspect-ratio: 4/3; overflow: hidden; }
.cert-card__image {
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.cert-card:hover .cert-card__image { transform: scale(1.05); }

.cert-card__overlay {
  position: absolute; inset: 0;
  background: rgba(0, 212, 255, 0.15);
  backdrop-filter: blur(2px);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px;
  opacity: 0; transition: opacity 0.3s ease;
  color: var(--color-accent);
}
.cert-card:hover .cert-card__overlay { opacity: 1; }
.cert-card__overlay span { font-size: 13px; font-weight: 600; }

.cert-card__info { padding: 8px 10px; }
.cert-card__issuer { margin-bottom: 3px; }
.cert-card__title {
  font-size: 11px; font-weight: 600;
  color: var(--color-text);
  line-height: 1.35;
}

/* Lightbox */
.lightbox {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.9);
  backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  gap: 16px;
}
.lightbox__content {
  max-width: 800px; width: 100%;
  display: flex; flex-direction: column; align-items: center; gap: 16px;
}
.lightbox__image {
  max-height: 70vh; max-width: 100%;
  border-radius: var(--radius);
  box-shadow: 0 32px 80px rgba(0,0,0,0.8);
  object-fit: contain;
}
.lightbox__meta { text-align: center; }
.lightbox__title {
  font-size: 18px; font-weight: 700;
  color: var(--color-text); margin: 8px 0 4px;
}
.lightbox__counter { font-size: 12px; color: var(--color-text-muted); font-family: var(--font-mono); }

.lightbox__close {
  position: absolute; top: 20px; right: 20px;
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 50%;
  color: var(--color-text);
  cursor: pointer;
  transition: var(--transition);
}
.lightbox__close:hover { background: rgba(255,255,255,0.1); border-color: var(--color-accent); }

.lightbox__nav {
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 50%;
  color: var(--color-text);
  cursor: pointer;
  transition: var(--transition);
  flex-shrink: 0;
}
.lightbox__nav:hover { background: var(--color-bg-card-hover); border-color: var(--color-accent); color: var(--color-accent); }

/* Transitions */
.lightbox-enter-active, .lightbox-leave-active { transition: opacity 0.25s ease; }
.lightbox-enter-from, .lightbox-leave-to { opacity: 0; }

/* Responsive */
@media (max-width: 768px) {
  .certs__grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }
  .cert-card__info { padding: 10px 12px; }
  .cert-card__title { font-size: 12px; }
  .lightbox { padding: 16px; gap: 10px; }
  .lightbox__title { font-size: 15px; }
  .lightbox__nav { width: 36px; height: 36px; }
  .lightbox__close { top: 12px; right: 12px; width: 36px; height: 36px; }
  .lightbox__image { max-height: 60vh; border-radius: 10px; }
}
@media (max-width: 480px) {
  .certs__grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  .lightbox { flex-direction: column; padding: 60px 12px 12px; justify-content: flex-start; }
  .lightbox__content { gap: 10px; }
  .lightbox__image { max-height: 55vh; }
  .lightbox__nav {
    position: absolute;
    bottom: 16px;
    width: 40px; height: 40px;
  }
  .lightbox__nav--prev { left: 50%; transform: translateX(calc(-100% - 8px)); }
  .lightbox__nav--next { left: 50%; transform: translateX(8px); }
}
</style>
